import {
  type Control,
  type FieldValues,
  type Path,
  type UseFormRegister,
  useWatch,
} from "react-hook-form";

import { Input } from "components/ui/input";
import { Label } from "components/ui/label";

import { CUSTOMER_FACING_NAME_MAX } from "../constants/campaign-name";
import { CUSTOMER_FACING_COPY } from "../constants/messages";

const FIELD_ID = "customerFacingName";
const HELP_ID = "customerFacingName-help";
const ERROR_ID = "customerFacingName-error";

interface CustomerFacingNameFieldProps<T extends FieldValues> {
  control: Control<T>;
  register: UseFormRegister<T>;
  errorMessage?: string;
}

export function CustomerFacingNameField<T extends FieldValues>({
  control,
  register,
  errorMessage,
}: CustomerFacingNameFieldProps<T>): JSX.Element {
  const name = "mechanicConfig.presentation.customerFacingName" as Path<T>;
  const value: unknown = useWatch({ control, name });
  const length: number = typeof value === "string" ? value.length : 0;
  const describedBy: string = errorMessage ? `${HELP_ID} ${ERROR_ID}` : HELP_ID;

  return (
    <div>
      <div className="flex items-center justify-between">
        <Label htmlFor={FIELD_ID}>{CUSTOMER_FACING_COPY.nameLabel}</Label>
        <span className="text-xs text-gray-500">
          {length}/{CUSTOMER_FACING_NAME_MAX}
        </span>
      </div>
      <Input
        id={FIELD_ID}
        placeholder={CUSTOMER_FACING_COPY.namePlaceholder}
        maxLength={CUSTOMER_FACING_NAME_MAX}
        aria-describedby={describedBy}
        aria-invalid={Boolean(errorMessage)}
        {...register(name)}
      />
      <p id={HELP_ID} className="mt-1 text-xs text-gray-500">
        {CUSTOMER_FACING_COPY.nameHelp}
      </p>
      {errorMessage && (
        <p id={ERROR_ID} className="mt-1 text-sm text-red-500">
          {errorMessage}
        </p>
      )}
    </div>
  );
}
